<template>
  <div class="grid-x full-width admin-page-container">
    <div class="cell small-12 medium-10 medium-offset-1 large-10 large-offset-1 content-section">

      <!-- Dark Glassmorphic Top Container -->
      <div class="glass-card">

        <!-- Header & Action Row -->
        <div class="header-action-row grid-x align-middle">
          <div class="cell small-12 medium-3 text-left">
            <h1 class="page-title">User Management</h1>
            <span class="subtitle">System directory & access control.</span>
          </div>

          <!-- Search Filter Input -->
          <div class="cell small-12 medium-4 search-cell">
            <div class="search-input-wrapper">
              <svg class="search-icon-svg" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input type="text" v-model="searchQuery" placeholder="Search by name or email..." class="search-field" />
              <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search" title="Clear search"><i
                  class="fa-solid fa-xmark"></i></button>
            </div>
          </div>

          <div class="cell small-12 medium-5 text-right action-buttons">
            <button @click="refreshUsers" class="btn-refresh" :disabled="loading">
              <i class="fa-solid fa-rotate" :class="{ 'fa-spin': loading }"></i>
              <span class="btn-text-wrapper">
                <transition name="fade-slide" mode="out-in">
                  <span :key="loading" class="btn-text">
                    {{ loading ? 'Syncing...' : 'Refresh' }}
                  </span>
                </transition>
              </span>
            </button>


            <button @click="openAddModal" class="btn-add-user">
              <i class="fa-solid fa-user-plus" style="margin-right: 0.4rem;"></i> Add New User
            </button>
          </div>
        </div>

        <!-- Quick Stats Overview Bar -->
        <div class="stats-row grid-x">
          <div class="cell small-4 stat-card">
            <span class="stat-label">Total Users</span>
            <span class="stat-value">{{ users.length }}</span>
          </div>
          <div class="cell small-4 stat-card">
            <span class="stat-label">Active Today</span>
            <span class="stat-value text-success">{{ activeTodayCount }}</span>
          </div>
          <div class="cell small-4 stat-card">
            <span class="stat-label">Total Logins</span>
            <span class="stat-value text-accent">{{ totalLoginsCount }}</span>
          </div>
        </div>

        <!-- Main User Table Container -->
        <div class="table-card">
          <div class="table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th>USER</th>
                  <th>EMAIL</th>
                  <th>ROLE</th>
                  <th>LOGINS</th>
                  <th>LAST ACTIVE</th>
                  <th class="text-right">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <!-- Loading State -->
                <tr v-if="loading && users.length === 0">
                  <td colspan="6" class="text-center padding-3">
                    <p class="status-msg">Fetching user directory...</p>
                  </td>
                </tr>

                <!-- Search Empty State -->
                <tr v-else-if="!loading && filteredUsers.length === 0 && searchQuery">
                  <td colspan="6" class="text-center padding-3">
                    <div class="empty-search-state">
                      <p class="status-msg">No users found matching "<strong>{{ searchQuery }}</strong>"</p>
                      <button @click="searchQuery = ''" class="btn-clear-filter">Clear search filter</button>
                    </div>
                  </td>
                </tr>

                <!-- General Empty State -->
                <tr v-else-if="!loading && users.length === 0">
                  <td colspan="6" class="text-center padding-3">
                    <p class="status-msg">No users found in database.</p>
                  </td>
                </tr>

                <!-- User Rows -->
                <tr v-for="(user, index) in filteredUsers" :key="user.id" :class="{ 'zebra-row': index % 2 !== 0 }">
                  <td class="user-cell">
                    <div class="avatar-wrapper">
                      <img v-if="user.picture && !imageErrors[user.id]" :src="user.picture" class="user-avatar"
                        alt="Avatar" referrerpolicy="no-referrer" @error="handleImageError(user.id)" />
                      <div v-else class="avatar-fallback"
                        :style="{ backgroundColor: getAvatarColor(user.name || user.email) }">
                        {{ getInitials(user.name || user.email) }}
                      </div>
                    </div>
                    <div class="user-info-stack">
                      <span class="user-name">{{ user.firstname }} {{ user.lastname }}</span>
                      <span class="mobile-email">{{ user.email }}</span>
                    </div>
                  </td>

                  <td class="user-email">{{ user.email }}</td>

                  <td>
                    <span class="role-badge" :class="user.role ? user.role.toLowerCase().replace(/\s+/g, '-') : 'user'">
                      {{ user.role || 'USER' }}
                    </span>
                  </td>

                  <td class="login-count">
                    <span :class="user.logins > 0 ? 'login-chip' : 'text-muted'">{{ user.logins }}</span>
                  </td>

                  <td class="date-cell">
                    <div class="activity-cell">
                      <span class="status-dot" :class="{ active: isRecentActive(user.lastLogin) }"></span>
                      <span>{{ formatDate(user.lastLogin) }}</span>
                    </div>
                  </td>

                  <td class="text-right">
                    <button @click="openEditModal(user)" class="btn-edit-link">
                      <i class="fa-solid fa-pen-to-square" style="margin-right: 0.3rem;"></i> Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

    </div>

    <!-- Modal: Add / Edit User -->
    <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
      <div class="modal-content animate-in">
        <div class="modal-header grid-x align-justify align-middle">
          <h3 class="cell auto">{{ isEditing ? 'Edit User Profile' : 'Add New User' }}</h3>
          <button class="cell shrink button-close" @click="closeModal">&times;</button>
        </div>

       <form @submit.prevent="saveUser" class="modal-body">
          <div class="form-group">
            <label>First Name</label>
            <input type="text" v-model="userForm.firstname" placeholder="First name" />
          </div>

          <div class="form-group">
            <label>Last Name</label>
            <input type="text" v-model="userForm.lastname" placeholder="Last name" />
          </div>

          <div class="form-group">
            <label>Email Address</label>
            <input type="email" v-model="userForm.email" placeholder="user@blything.co.uk" :disabled="isEditing"
              :required="!isEditing" />
          </div>

          <div class="form-group">
            <label>Profile Picture URL</label>
            <input type="url" v-model="userForm.picture" placeholder="https://example.com/avatar.jpg" />
          </div>

          <!-- Address Fields Block -->
          <div class="form-group">
            <label>Address Line 1</label>
            <input type="text" v-model="userForm.location.address_line1" placeholder="Street address" />
          </div>

          <div class="form-group">
            <label>Address Line 2</label>
            <input type="text" v-model="userForm.location.address_line2" placeholder="Apartment, suite, etc." />
          </div>

          <div class="form-group">
            <label>Address Line 3</label>
            <input type="text" v-model="userForm.location.address_line3" placeholder="City / Town" />
          </div>

          <div class="form-group grid-x grid-margin-x">
            <div class="cell medium-6">
              <label>Country</label>
              <input type="text" v-model="userForm.location.country" placeholder="Country" />
            </div>
            <div class="cell medium-6">
              <label>Postcode</label>
              <input type="text" v-model="userForm.location.postcode" placeholder="Postcode" />
            </div>
          </div>

          <div v-if="!isEditing" class="form-group">
            <label>Temporary Password</label>
            <input type="password" v-model="userForm.password" placeholder="Enter secure temporary password" required />
          </div>

          <div class="form-group">
            <label>Role Assignment</label>
            <select v-model="userForm.role">
              <option value="User">User</option>
              <option value="Admin">Admin</option>
            </select>
          </div>

          <div class="modal-footer text-right">
            <button type="button" class="btn-modal-cancel" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-modal-submit" :disabled="saving">
              {{ saving ? 'Saving Changes...' : (isEditing ? 'Save Changes' : 'Create User') }}
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useUserDirectory } from '@/composables/useUserDirectory';

const { users, loading, error, fetchUsers, saveUser: saveUserApi } = useUserDirectory();

const imageErrors = ref({});
const showModal = ref(false);
const isEditing = ref(false);
const saving = ref(false);
const searchQuery = ref('');
const userForm = ref({
  id: '',
  name: '',
  email: '',
  picture: '',
  role: 'User',
  password: '',
  firstname: '',
  lastname: '',
  location: {
    address_line1: '',
    address_line2: '',
    address_line3: '',
    country: '',
    postcode: ''
  }
});

const filteredUsers = computed(() => {
  if (!searchQuery.value.trim()) return users.value;
  const query = searchQuery.value.toLowerCase();
  return users.value.filter(u =>
    (u.name && u.name.toLowerCase().includes(query)) ||
    (u.email && u.email.toLowerCase().includes(query))
  );
});

const activeTodayCount = computed(() => {
  const today = new Date().toDateString();
  return users.value.filter(u => u.lastLogin && new Date(u.lastLogin).toDateString() === today).length;
});

const totalLoginsCount = computed(() => {
  return users.value.reduce((acc, u) => acc + (Number(u.logins) || 0), 0);
});

const refreshUsers = () => fetchUsers(true);

const openAddModal = () => {
  isEditing.value = false;
  userForm.value = { id: '', name: '', email: '', picture: '', role: 'User', password: '' };
  showModal.value = true;
};

const openEditModal = (user) => {
  isEditing.value = true;
  userForm.value = { ...user };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const saveUser = async () => {
  saving.value = true;
  try {
    await saveUserApi(userForm.value, isEditing.value);
    closeModal();
  } catch (err) {
    console.error('Save User Failed:', err);
    alert(`Error saving user: ${err.message}`);
  } finally {
    saving.value = false;
  }
};

const handleImageError = (userId) => {
  imageErrors.value[userId] = true;
};

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const getAvatarColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 40%)`;
};

const isRecentActive = (d) => {
  if (!d) return false;
  const today = new Date().toDateString();
  return new Date(d).toDateString() === today;
};

const formatDate = (d) => {
  if (!d) return 'Never';
  const date = new Date(d);
  return isNaN(date.getTime()) ? d : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

onMounted(() => {
  fetchUsers();
});
</script>

<style lang="scss" scoped>
@use "@/assets/styles/abstract/variables" as *;

$bg-dark: #0b0f19;
$card-dark: rgba(17, 24, 39, 0.75);
$border-dark: rgba(255, 255, 255, 0.08);
$accent-cyan: #38bdf8;
$text-light: #f8fafc;
$text-muted: #94a3b8;

h1.page-title {
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.2;
  white-space: nowrap;
  text-align: left;
}

.subtitle {

  color: $text-light;
  opacity: 0.7; // Softened slightly for a nice subtext balance
  display: block;
  margin-top: 0.1rem;
}

.admin-page-container {
  padding-top: 1.5rem;
  padding-bottom: 3rem;

  .glass-card {
    background: $card-dark;
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid $border-dark;
    border-radius: 18px;
    padding: 1.75rem 2rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  }

  .header-action-row {
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap; // Prevent items from dropping lines awkwardly
    gap: 1rem;

    .title-cell {
      flex: 0 0 auto;




    }

    .search-cell {
      flex: 1 1 auto;
      max-width: 400px;
      padding: 0;
      display: flex;
      align-items: center;

      .search-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        background: rgba(15, 23, 42, 0.9);
        border: 1px solid $border-dark;
        border-radius: 9px;
        padding: 0.45rem 0.8rem; // Controls vertical size naturally via padding
        transition: all 0.2s ease;

        &:focus-within {
          border-color: $accent-cyan;
          background: rgba(15, 23, 42, 1);
          box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.15);
        }

        .search-icon-svg {
          display: flex;
          align-items: center;
          justify-content: center;
          color: $text-muted;
          pointer-events: none;
          margin-right: 0.6rem;
          flex-shrink: 0;
        }

        .search-field {
          flex: 1;
          width: 100%;
          background: transparent;
          border: none;
          padding: 0;
          margin: 0;
          font-size: 0.85rem;
          line-height: 1.5;
          color: $text-light;

          &::placeholder {
            color: rgba(148, 163, 184, 0.5);
          }

          &:focus {
            outline: none;
            box-shadow: none;
          }
        }

        .clear-search {
          background: none;
          border: none;
          font-size: 0.8rem;
          color: $text-muted;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.2rem;
          margin-left: 0.5rem;
          flex-shrink: 0;
          line-height: 1;

          &:hover {
            color: $text-light;
          }
        }
      }
    }

    .action-buttons {
      flex: 0 0 auto;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 0.5rem;

      button {
        border-radius: 8px;
        padding: 0.55rem 1rem;
        font-size: 0.82rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
      }

      .btn-refresh {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        background: rgba(255, 255, 255, 0.04);
        color: $text-muted;
        border: 1px solid $border-dark;

        .btn-text-wrapper {
          display: inline-block;
          position: relative;
          min-width: 52px; // Prevents button from resizing abruptly when text changes length
          text-align: center;
        }

        // Vue transition animation classes
        .fade-slide-enter-active,
        .fade-slide-leave-active {
          transition: all 0.15s ease;
        }

        .fade-slide-enter-from {
          opacity: 0;
          transform: translateY(4px);
        }

        .fade-slide-leave-to {
          opacity: 0;
          transform: translateY(-4px);
        }

        &:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.08);
          color: $text-light;
          border-color: rgba(255, 255, 255, 0.15);
        }
      }

      .btn-add-user {
        background: $accent-cyan;
        color: #0f172a;
        border: 1px solid transparent;
        font-weight: 700;

        &:hover {
          background: lighten($accent-cyan, 6%);
          box-shadow: 0 0 15px rgba(56, 189, 248, 0.4);
        }
      }
    }
  }

  /* Quick Stats Row Cards */
  .stats-row {
    margin-bottom: 1.25rem;
    gap: 0;

    .stat-card {
      background: rgba(15, 23, 42, 0.6);
      border: 1px solid $border-dark;
      padding: 0.75rem 1rem;
      border-radius: 10px;
      margin-right: 0.75rem;
      flex: 1;
      display: flex;
      flex-direction: column;

      &:last-child {
        margin-right: 0;
      }

      .stat-label {
        font-size: 0.7rem;
        font-weight: 700;
        color: $text-muted;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .stat-value {
        font-size: 1.25rem;
        font-weight: 800;
        color: $text-light;
        margin-top: 0.1rem;

        &.text-success {
          color: #34d399;
        }

        &.text-accent {
          color: $orange;
        }
      }
    }
  }

  .table-card {
    background: rgba(15, 23, 42, 0.6);
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    border: 1px solid $border-dark;
    overflow: hidden;

    .table-wrapper {
      overflow-x: auto;

      .custom-table {
        width: 100%;
        border-collapse: collapse;
        text-align: left;
        background: transparent;
        border: none;

        thead {
          border: none;

          th {
            padding: 1rem 1.25rem;
            font-size: 0.7rem;
            font-weight: 700;
            color: $text-muted;
            letter-spacing: 0.08em;
            border-bottom: 2px solid rgba(255, 255, 255, 0.06);
            border-top: none;
            border-left: none;
            border-right: none;
            background: rgba(15, 23, 42, 0.8);
          }
        }

        tbody {
          background: transparent;
          border: none;

          tr {
            transition: all 0.15s ease;
            position: relative;
            background: transparent;
            border: none;

            td {
              padding: 0.9rem 1.25rem;
              vertical-align: middle;
              font-size: 0.88rem;
              border-bottom: 1px solid rgba(255, 255, 255, 0.04);
              border-top: none;
              border-left: none;
              border-right: none;
              color: $text-light;
            }

            &:last-child {
              td {
                border-bottom: none;
              }
            }

            &:hover {
              background-color: rgba(30, 41, 59, 0.6);
              box-shadow: inset 3px 0 0 $accent-cyan;
            }
          }
        }

        .user-cell {
          display: flex;
          align-items: center;
          gap: 0.85rem;

          .avatar-wrapper {
            width: 38px;
            height: 38px;
            min-width: 38px;
            min-height: 38px;
            border-radius: 50%;
            overflow: hidden;
            flex-shrink: 0;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
            border: 2px solid rgba(255, 255, 255, 0.15);

            .user-avatar {
              width: 100%;
              height: 100%;
              object-fit: cover;
              display: block;
            }

            .avatar-fallback {
              width: 100%;
              height: 100%;
              color: #ffffff;
              font-weight: 700;
              font-size: 0.75rem;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }

          .user-name {
            font-weight: 700;
            color: $text-light;
            display: block;
          }

          .mobile-email {
            display: none;
            font-size: 0.75rem;
            color: $text-muted;
          }
        }

        .user-email {
          color: $text-muted;
          font-weight: 400;
        }

        .role-badge {
          display: inline-block;
          font-size: 0.7rem;
          font-weight: 700;
          padding: 0.25rem 0.75rem;
          border-radius: 50rem;
          background: rgba(255, 255, 255, 0.05);
          color: $text-muted;
          border: 1px solid rgba(255, 255, 255, 0.1);
          text-transform: uppercase;
          letter-spacing: 0.025em;

          &.admin {
            background: rgba(245, 158, 11, 0.15);
            color: #fbbf24;
            border-color: rgba(245, 158, 11, 0.3);
          }

          &.user {
            background: rgba(56, 189, 248, 0.15);
            color: #38bdf8;
            border-color: rgba(56, 189, 248, 0.3);
          }
        }

        .login-chip {
          background: rgba(255, 255, 255, 0.06);
          padding: 0.15rem 0.5rem;
          border-radius: 4px;
          font-weight: 600;
          color: $text-light;
        }

        .activity-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          .status-dot {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #475569;

            &.active {
              background: #34d399;
              box-shadow: 0 0 8px rgba(52, 211, 153, 0.8);
            }
          }
        }

        .date-cell {
          color: $text-muted;
          font-size: 0.85rem;
        }

        .btn-edit-link {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: $accent-cyan;
          font-size: 0.8rem;
          font-weight: 600;
          padding: 0.3rem 0.75rem;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;

          &:hover {
            background: $accent-cyan;
            color: #0f172a;
            border-color: $accent-cyan;
          }
        }

        .status-msg {
          color: $text-muted;
          margin: 0;
          padding: 1rem 0;
        }

        .empty-search-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 0;

          .btn-clear-filter {
            background: none;
            border: none;
            color: $accent-cyan;
            font-size: 0.82rem;
            font-weight: 600;
            cursor: pointer;
            text-decoration: underline;
          }
        }
      }
    }
  }
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(5, 7, 13, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  width: 100%;
  max-width: 460px;
  background: #111827;
  border-radius: 14px;
  padding: 1.75rem 2rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);

  .modal-header {
    margin-bottom: 1.25rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding-bottom: 0.75rem;

    h3 {
      font-size: 1.2rem;
      font-weight: 700;
      color: $text-light;
      margin: 0;
    }

    .button-close {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: $text-muted;
      cursor: pointer;
      line-height: 1;

      &:hover {
        color: $text-light;
      }
    }
  }

  .form-group {
    margin-bottom: 1.1rem;

    label {
      display: block;
      font-size: 0.8rem;
      font-weight: 600;
      color: $text-muted;
      margin-bottom: 0.35rem;
    }

    input,
    select {
      width: 100%;
      padding: 0.6rem 0.85rem;
      border: 1px solid $border-dark;
      border-radius: 8px;
      font-size: 0.9rem;
      color: $text-light;
      background: rgba(15, 23, 42, 0.9);
      transition: all 0.2s ease;

      &:focus {
        outline: none;
        border-color: $accent-cyan;
        background: #0f172a;
        box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.15);
      }

      &:disabled {
        background: rgba(255, 255, 255, 0.03);
        color: $text-muted;
        cursor: not-allowed;
      }
    }
  }

  .modal-footer {
    margin-top: 1.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding-top: 1rem;

    button {
      border-radius: 8px;
      padding: 0.6rem 1.2rem;
      font-weight: 600;
      font-size: 0.88rem;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .btn-modal-cancel {
      background: rgba(255, 255, 255, 0.05);
      color: $text-muted;
      border: 1px solid $border-dark;
      margin-right: 0.5rem;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: $text-light;
      }
    }

    .btn-modal-submit {
      background: $accent-cyan;
      color: #0f172a;
      border: 1px solid transparent;
      font-weight: 700;

      &:hover {
        background: lighten($accent-cyan, 6%);
        box-shadow: 0 0 15px rgba(56, 189, 248, 0.4);
      }
    }
  }
}
</style>